'use client';

/**
 * TimelineItem Component
 *
 * Individual event item for the RecordTimeline component.
 * Displays event details with icon, timestamp, actor, and optional field changes.
 */

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { eventTypeColors, getEventTypeColor, getNeonStyles, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { FieldChange, TimelineEvent } from '../../types/entityFramework';
import { formatDateTime, formatRelativeTime } from '../../utils/formatters';

// ============================================================================
// Types
// ============================================================================

export interface TimelineItemProps {
  /** The timeline event to display */
  event: TimelineEvent;
  /** Whether to show field changes */
  showFieldChanges?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Display variant */
  variant?: 'compact' | 'detailed';
  /** Whether to show the connecting line below */
  showConnector?: boolean;
  /** Whether this is the last item */
  isLast?: boolean;
}

// ============================================================================
// Event Type Configuration
// ============================================================================

type EventType = TimelineEvent['type'];

interface EventConfig {
  icon: ReactNode;
  color: string;
  label: string;
}

const getEventConfig = (type: EventType, isDark: boolean): EventConfig => {
  const configs: Record<EventType, EventConfig> = {
    created: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5v6M5 8h6" />
        </svg>
      ),
      color: eventTypeColors.created,
      label: 'Created',
    },
    updated: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 4L7 14M4 4l5 10" />
        </svg>
      ),
      color: eventTypeColors.updated,
      label: 'Updated',
    },
    deleted: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      ),
      color: eventTypeColors.deleted,
      label: 'Deleted',
    },
    comment: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M2 3h12v8H4l-2 2V3z" />
        </svg>
      ),
      color: eventTypeColors.comment,
      label: 'Comment',
    },
    status_change: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 8h10M10 5l3 3-3 3" />
        </svg>
      ),
      color: eventTypeColors.statusChange,
      label: 'Status Changed',
    },
    assignment: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="5" r="3" />
          <path d="M3 14c0-3 2.5-5 5-5s5 2 5 5" />
        </svg>
      ),
      color: eventTypeColors.assignment,
      label: 'Assigned',
    },
    custom: {
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="2" />
          <circle cx="8" cy="8" r="6" />
        </svg>
      ),
      color: getEventTypeColor('custom', isDark),
      label: 'Event',
    },
  };

  return configs[type] || configs.custom;
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $isLast: boolean;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ $isLast }) => ({
  display: 'flex',
  position: 'relative',
  paddingBottom: $isLast ? 0 : 24,
}));

interface StyledIconContainerProps {
  $color: string;
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledIconContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledIconContainerProps>(({ $color, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const bgColor = neonStyles ? neonStyles.background : `${$color}${$isDark ? '20' : '15'}`;
  const borderColor = neonStyles ? neonStyles.border : `${$color}${$isDark ? '40' : '30'}`;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: bgColor,
    border: `1px solid ${borderColor}`,
    color: neonStyles ? neonAccents[$neonAccent!].base : $color,
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
    transition: transitionPresets.all,
  };
});

interface StyledConnectorProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledConnector = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledConnectorProps>(({ $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    position: 'absolute',
    left: 17,
    top: 36,
    bottom: 0,
    width: 2,
    background: neonStyles ? neonStyles.border : $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
  };
});

interface StyledContentProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $variant: 'compact' | 'detailed';
}

const StyledContent = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContentProps>(({ $isDark, $neonAccent, $variant }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    flex: 1,
    marginLeft: 16,
    padding: $variant === 'detailed' ? 16 : 8,
    borderRadius: 12,
    background: neonStyles ? neonStyles.background : glass.background,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    transition: transitionPresets.card,

    '&:hover': {
      background: neonStyles ? `${neonStyles.background}` : glass.backgroundHover,
      border: `1px solid ${neonStyles ? neonStyles.border : glass.borderHover}`,
    },
  };
});

const StyledHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
});

const StyledExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $expanded: boolean }>(({ theme, $expanded }) => ({
  width: 24,
  height: 24,
  padding: 4,
  transition: transitionPresets.all,
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: theme.palette.text.secondary,

  '&:hover': {
    background: theme.palette.action.hover,
  },
}));

interface StyledFieldChangeProps {
  $isDark: boolean;
}

const StyledFieldChange = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledFieldChangeProps>(({ $isDark }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 12,
  borderRadius: 8,
  background: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
}));

const StyledValueBadge = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $type: 'old' | 'new';
  $isDark: boolean;
}>(({ $type, $isDark }) => {
  const colors =
    $type === 'old'
      ? {
          bg: $isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
          border: $isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
          text: '#ef4444',
        }
      : {
          bg: $isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
          border: $isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
          text: '#10b981',
        };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 6,
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
    maxWidth: '100%',
    overflow: 'hidden',

    '& .label': {
      color: colors.text,
      fontWeight: 600,
      flexShrink: 0,
    },

    '& .value': {
      color: $isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export function TimelineItem({
  event,
  showFieldChanges = true,
  neonAccent,
  variant = 'compact',
  showConnector = true,
  isLast = false,
}: TimelineItemProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState(variant === 'detailed');

  const eventConfig = useMemo(() => getEventConfig(event.type, isDark), [event.type, isDark]);

  const hasFieldChanges = event.fieldChanges && event.fieldChanges.length > 0 && showFieldChanges;
  const expandable = hasFieldChanges && variant === 'compact';

  const toggleExpanded = useCallback(() => {
    if (expandable) {
      setExpanded((prev) => !prev);
    }
  }, [expandable]);

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return formatDateTime(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <StyledContainer
      $isDark={isDark}
      $isLast={isLast}
      initial={motionPresets.fadeInUp.initial}
      animate={motionPresets.fadeInUp.animate}
      transition={{
        ...motionPresets.fadeInUp.transition,
        delay: 0.05,
      }}
    >
      {/* Icon */}
      <StyledIconContainer $color={eventConfig.color} $isDark={isDark} $neonAccent={neonAccent}>
        {eventConfig.icon}
      </StyledIconContainer>
      {/* Connector Line */}
      {showConnector && !isLast && <StyledConnector $isDark={isDark} $neonAccent={neonAccent} />}
      {/* Content */}
      <StyledContent $isDark={isDark} $neonAccent={neonAccent} $variant={variant}>
        <StyledHeader>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Event Description */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                mb: 0.5,
              }}
            >
              {event.description}
            </Typography>

            {/* Actor and Timestamp */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {event.userName && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: neonAccent ? neonAccents[neonAccent].base : 'primary.main',
                  }}
                >
                  {event.userName}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
                title={formatDateTime(event.timestamp)}
              >
                {formatRelativeTime(event.timestamp)}
              </Typography>
            </Stack>
          </Box>

          {/* Expand Button */}
          {expandable && (
            <StyledExpandButton $expanded={expanded} onClick={toggleExpanded} size="small">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </StyledExpandButton>
          )}
        </StyledHeader>

        {/* Field Changes */}
        {hasFieldChanges && (
          <Collapse in={expanded || variant === 'detailed'}>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {event.fieldChanges!.map((change, index) => (
                <FieldChangeDisplay
                  key={`${change.field}-${index}`}
                  change={change}
                  isDark={isDark}
                  formatValue={formatValue}
                />
              ))}
            </Stack>
          </Collapse>
        )}
      </StyledContent>
    </StyledContainer>
  );
}

// ============================================================================
// Field Change Display Sub-component
// ============================================================================

interface FieldChangeDisplayProps {
  change: FieldChange;
  isDark: boolean;
  formatValue: (value: unknown) => string;
}

function FieldChangeDisplay({ change, isDark, formatValue }: FieldChangeDisplayProps) {
  return (
    <StyledFieldChange $isDark={isDark}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          letterSpacing: '0.08em',
        }}
      >
        {change.fieldLabel}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: 'wrap',
        }}
      >
        <StyledValueBadge $type="old" $isDark={isDark}>
          <span className="label">From:</span>
          <span className="value">{formatValue(change.oldValue)}</span>
        </StyledValueBadge>

        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}
          strokeWidth="1.5"
          style={{ alignSelf: 'center', flexShrink: 0 }}
        >
          <path d="M2 6h8M7 3l3 3-3 3" />
        </svg>

        <StyledValueBadge $type="new" $isDark={isDark}>
          <span className="label">To:</span>
          <span className="value">{formatValue(change.newValue)}</span>
        </StyledValueBadge>
      </Stack>
    </StyledFieldChange>
  );
}

TimelineItem.displayName = 'TimelineItem';

export default TimelineItem;
